// Dependencies
import { useState, useEffect, useContext, useRef, useCallback, forwardRef, useImperativeHandle } from "react";
import {  } from 'react-use';
import { useResizeDetector } from "react-resize-detector";
import { useFormik, FormikProvider } from 'formik';
import _cx from 'classnames'; 

// Components
import FormSection from './Form/Sections'

// Contexts
import AppContext from '../contexts/AppContext';

// Hooks
import useAPI from '../hooks/Api';
import useDebug from "../hooks/Wooptima/Debug";
import useConditionalLogic from '../hooks/ConditionalLogic';
    
// Types
import { Types, Components } from '../utils/types';
import { FormikConfig, FormikValues } from 'formik';

// Utils
import {  } from '../utils/config'
import {  } from '../utils/helpers'
import _ from "lodash"; 
import { CSSTransition } from "react-transition-group";

const Form = forwardRef(({sections: formSections, onFormSubmit}: Components.Form, ref) => {

    const { logDebugMessage } = useDebug('Form');

    // TODO:    Do this dynamically
    useImperativeHandle(ref, () => ({

        resetForm() {
            formik.resetForm();

            let newFormSection = Object.assign({}, formSections[0]);
            newFormSection.groups = [ newFormSection.groups[0] ];

            setActiveFormSections( [ newFormSection] );
        }
    
    }));

    const validateFormGroup = (formGroup: Types.FormGroup, formValues: { [key: string]: string } ) => {

        let error: string = '';

        // Skip if not contained in values
        if ( ! (formGroup.name in formValues) ) {
            return error;
        }

        // 
        if ( _.isEmpty( formValues[formGroup.name] ) ) {
            error = `${formGroup.name} is empty`;
            return error;
        }

        switch ( formGroup.type ) {

            case 'number':

                if ( ! formValues[formGroup.name].match(/^[0-9]*(|\.[0-9]*)$/) ) {
                    error = `${formGroup.name} not a valid number.`;
                }

                break;

            case 'email':

                if ( ! formValues[formGroup.name].match(/^\w+([-+.']\w+)*@\w+([-.]\w+)*\.\w+([-.]\w+)*$/) ) {
                    error = `${formGroup.name} not a valid email.`;
                }
                
                break;

            case 'timerange':

                if ( ! formValues[formGroup.name].match(/^((?:[01]\d:[0-5][0-9]|2[0-3]:[0-5][0-9])(?:\s?)-(?:\s?)(?:[01]\d:[0-5][0-9]|2[0-3]:[0-5][0-9])(?:\s?,\s?)?)+$/) ) {
                    error = `${formGroup.name} not a valid timerange.`;
                }
                
                break;
                
            case 'tel':

                if ( ! formValues[formGroup.name].match(/^(\+4219)\d{8}$/) ) {
                    error = `${formGroup.name} not a valid phone.`;
                }
                
                break;

        }

        return error;

    };

    const validateForm = (formValues: { [key: string]: string }, formSections: Types.FormSection[] | undefined = undefined) => {
            
        const errors: { [key: string]: string } = {};
        formSections = _.isUndefined( formSections ) ? activeFormSections : formSections;

        activeFormSections.forEach((formSection: Types.FormSection) => {

            formSection.groups.forEach((formGroup: Types.FormGroup) => {
               const foundError = validateFormGroup(formGroup, formValues);
               if ( !_.isEmpty( foundError ) ) {
                   errors[formSection.name] = foundError;
               }
               return;
            })
            
        })

        return errors;

    }

    const getPreviousFormGroup = (formSection: Types.FormSection, currentFormGroupOrder: number) => {
        let i = 1;
        
        while ( ! checkConditions( formSection?.groups[currentFormGroupOrder - i]?.conditions ) ) i++;
        return formSection.groups[currentFormGroupOrder - i];
    }

        // TODO: First update values then update sections...
        const updateForm = () => {

            let newFormSections: Types.FormSection[] = [];
            let newFormValues: { [key: string]: any } = {};
    
            let submitSectionIndex: number|null = null;
            const sectionsToSkip: string[] = [];
    
            // 1  Loop over each formSection
            formSections.forEach( (formSection: Types.FormSection, sectionOrder: number) => {
    
                if ( sectionsToSkip.includes( formSection.name ) ) {
                    return;
                }
    
                // 1.1  Handle final section
                if ( formSection?.type === 'submit'  ) {
                    submitSectionIndex = sectionOrder;
                    return;
                }
                
                // 1.2  Skip formSection if it doesn't passed the conditional check
                if ( ! checkConditions( formSection?.conditions ) ) {  
                    logDebugMessage( `Form section ${formSection.name} does not passed through the condition check.` );
                    return;
                }
    
                // 2.   Create a new instance of the formSection
                let newFormSection: Types.FormSection = Object.assign({}, formSection); // Fuck you JS
                newFormSection.groups = [];
    
                // 2.1  Loop over each formGroup of the formSection
                formSection.groups.forEach( (formGroup: Types.FormGroup, order: number) => {
    
                    // 2.2  Skip formGroup if it doesn't passed the conditional check
                    if ( ! checkConditions( formGroup?.conditions ) ) {
                        logDebugMessage( `Form group ${formGroup.name} does not passed through the condition check.` );
                        return;
                    }
                    
                    // 3.   Create a new instance of the formGroup
                    let newFormGroup: Types.FormGroup = Object.assign({}, formGroup); // Fuck you JS;
    
                    newFormValues[newFormGroup.name] = '';
    
                    const previousFormGroup: Types.FormGroup = getPreviousFormGroup(formSection, order);                
    
                    if ( order > 0 && previousFormGroup?.name && ( ! _.isBoolean(formSection?.stepByStep) || formSection?.stepByStep === true ) ) {
    
                        const previousValue: string = formik.values[ previousFormGroup?.name ];
    
                        if ( _.isEmpty( previousValue ) && !_.isNumber( previousValue ) ) {
                            return;
                        }
    
                    }                    
    
                    if ( newFormGroup.options && ['checkbox', 'radio', 'dropdown'].includes(formGroup.type) ) {
    
                        newFormGroup.options = newFormGroup.options.filter((option: Types.FormOption) => {
    
                            if ( ! checkConditions( option?.conditions, 'OR' ) ) {
                                logDebugMessage( `Option ${option.value} of the form group ${formGroup.name} does not passed through the condition check.` );
                                if ( option.sections ) {
                                    sectionsToSkip.push(...option.sections);
                                }
                                return false;
                            }
    
                            if ( newFormGroup.name in formik.values ) {
                                
                                if ( ['checkbox'].includes(formGroup.type) ) {
    
                                    if ( formik.values[newFormGroup.name].includes(option.value) ) {
                                        if ( _.isArray( newFormValues[newFormGroup.name] ) ) {
                                            newFormValues[newFormGroup.name].push(option.value);
                                        } else {
                                            newFormValues[newFormGroup.name] = [option.value];
                                        }
                                    }
    
                                } else {
                                    newFormValues[newFormGroup.name] = formik.values[newFormGroup.name];
                                }
    
                            }
    
                            return true;
    
                        })
    
                    } else {
    
                        if ( newFormGroup.name in formik.values ) {
                            newFormValues[newFormGroup.name] = formik.values[newFormGroup.name];
                        }
    
                    }
    
                    newFormSection.groups.push(newFormGroup);
    
                })
    
                // 1.X  Include section in the newFormSections
                if ( ! _.isEmpty( newFormSection.groups ) ) {
                    // 1.X
                    newFormSections.push( newFormSection );
                }
    
            });
            
            const formValid = areFormFieldsValid(newFormValues);
            toggleFormValid( formValid );
    
            if ( submitSectionIndex && formValid ) {
                formSections[submitSectionIndex].groups.forEach((formGroup: Types.FormGroup, order: number) => {
                    newFormValues[formGroup.name] = formik.values[formGroup.name];
                })
                newFormSections.push( formSections[submitSectionIndex] );
            }
    
            setActiveFormSections(newFormSections);
            formik.setValues(newFormValues, false);
    
        };

    const submitForm = (values: FormikValues) => {
        
        toggleFormLoading(true)
        
        APIController.submitForm( values )
        .then((response) => {
                formik.setSubmitting(false);
                toggleFormLoading(false)
                onFormSubmit(response);
            })
            .catch(errors => {
                logDebugMessage(errors)
                onFormSubmit({
                    isValid: false
                });
        })

    }

    const areFormFieldsValid = (newFormValues: any) => {

        if ( Object.keys(newFormValues).length === 0 ) {
            return false;
        }

        let validFieldsCount: number = 0;

        for ( let [name, value] of Object.entries( newFormValues ) ) {

            // TEMP: HOTFIX: Dirty hacky way of doing this, but it works...
            if ( name === 'lightsOffTimeRange' && !_.isEmpty( validateFormGroup({name, label: 'VO je vypnuté v čase od - do', type: 'timerange'}, newFormValues) ) ) {
                continue;
            } 

            if ( ! ['contactEmail', 'contactTel'].includes(name) && _.isEmpty( value ) && !_.isNumber( value ) ) {
                continue;
            }

            validFieldsCount++;

        }

        return Object.keys(newFormValues).length === validFieldsCount;

    }

    // Formik config
    const formikConfig: FormikConfig<FormikValues> = { 
        initialValues: {},
        onSubmit: submitForm,
        validate: validateForm
    }

    // States
    const [activeFormSections, setActiveFormSections] = useState<Types.FormSection[]>([]);
    const [formHeight, setFormHeight] = useState<null | number>(0);

    // Hooks
    const APIController = useAPI();
    const formik = useFormik( formikConfig );

    const { isFocused: isFormFocused, toggleFocus: toggleFormFocus, isScrollable, toggleScroll: toggleFormScroll, isValid: isFormValid, toggleValid: toggleFormValid, isLoading: isFormLoading, toggleLoading: toggleFormLoading } = useContext( AppContext );
    const [ checkConditions ] = useConditionalLogic(formik.values);
    
    const formRef = useRef<any>(null);
            
    // Effects

    useEffect(() => {

        if ( isFormFocused ) {
            return;
        }        

        if ( ! _.isEmpty( formik.touched ) ) {            
            toggleFormFocus(true);
        }

    }, [formik.touched])

    useEffect(() => {

        updateForm();
        // setActiveFormSections( updateForm );

        requestAnimationFrame(() => {

            const formSectionsEls = Array.from( formRef.current.querySelectorAll('.form-section') );
            
            if ( ! _.isEmpty( formSectionsEls ) ) {
                
                let height: number = formSectionsEls.reduce( (height: number, element: any) => {
                    return height + element.scrollHeight;
                }, 20);
    
                setFormHeight( height );

                setTimeout(() => {
                    setFormHeight( null );
                }, 1000)

            }

        })

    }, [formSections]);

    useEffect(() => {

        if ( formik.isValidating ) {
            updateForm();
        }


    }, [formik.values]);

    useEffect(() => {
        
        if ( isFormFocused ) {
            setTimeout(() => {
                toggleFormScroll(true);
            }, 750)
        }

    }, [isFormFocused])

    return (
        <>
            <FormikProvider value={formik}> 

                <form ref={formRef} className={_cx( "form__sections")} onSubmit={formik.handleSubmit}>
                    { activeFormSections.map( (formSection: Types.FormSection, order: number ) => <FormSection key={order} {...formSection} />) }
                </form>

                <CSSTransition mountOnEnter unmountOnExit in={isFormLoading && formik.isSubmitting && isFormValid} timeout={200} className="form__overlay">
                    <div>
                        <span className="form__overlay--spinner"></span> 
                    </div>
                </CSSTransition>
                
            </FormikProvider>
        </>
    ) 

})
    
export default Form; 